import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, List, Map as MapIcon, MessageSquare, ArrowUpRight } from "lucide-react";
import { MLS_SEARCH_URL } from "@/lib/guides";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HomeMap } from "@/components/map/HomeMap";
import { boundsOf, zoomForRadius } from "@/components/map/geo";
import { FilterBar } from "@/components/listings/FilterBar";
import { AiSearchBar } from "@/components/search/AiSearchBar";
import { ListingCard } from "@/components/listings/ListingCard";
import { ActiveListingCard } from "@/components/listings/ActiveListingCard";
import { MarketSnapshot } from "@/components/listings/MarketSnapshot";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { useBases, useListings, useMeta } from "@/hooks/useData";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/context/AuthContext";
import { LIFESTYLES } from "@/lib/site";

const SORTS = [["newest", "Newest"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["sqft_desc", "Largest"], ["distance", "Closest to base"]];
const inBounds = (l, b) => l.lng >= b.west && l.lng <= b.east && l.lat >= b.south && l.lat <= b.north;

const SearchForMe = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="flex w-full items-center gap-4 rounded-md border border-dashed border-neutral-300 p-5 text-left transition-colors hover:border-ink" data-testid="search-for-me-open">
        <MessageSquare className="h-6 w-6 shrink-0 text-brass-deep" />
        <span><span className="block text-[16px] font-medium">Want us to search for you?</span>
          <span className="text-[14px] text-neutral-600">Tell us your base, budget and must-haves — we'll send homes that fit.</span></span>
      </button>
    </DialogTrigger>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader><DialogTitle className="text-2xl font-normal">Personalized home search</DialogTitle></DialogHeader>
      <EnquiryForm kind="buy" extras={["base", "reportDate"]} dense submitLabel="Send my search" testIdPrefix="search-for-me"
        messagePlaceholder="Beds, budget, school needs, commute limits…" />
    </DialogContent>
  </Dialog>
);

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries([...sp.entries()]), [sp]);
  const [aiQuery] = useState(() => sp.get("ai") || "");
  const [aiSummary, setAiSummary] = useState("");
  const apiParams = useMemo(() => {
    const p = { ...filters };
    delete p.saved;
    delete p.ai;
    if (p.base && !p.radius) p.radius = "15";
    return p;
  }, [filters]);
  const { data, loading } = useListings(apiParams);
  const bases = useBases();
  const { meta } = useMeta();
  const { saved } = useFavorites();
  const { user, requireAccount } = useAuth();
  const [hoverKey, setHoverKey] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [view, setView] = useState("list");
  const [bounds, setBounds] = useState(null);
  const [boundsFilter, setBoundsFilter] = useState(false);
  const [focus, setFocus] = useState(null);
  const base = bases.find((b) => b.id === filters.base);
  const radius = base ? Number(apiParams.radius) : null;

  const update = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? next.delete(k) : next.set(k, String(v))));
    if (patch.saved && !user) {
      requireAccount("Create a free account to save homes and see them here on any device.");
      return;
    }
    setAiSummary("");
    setSp(next, { replace: true });
  };
  const applyAi = (params, summary) => {
    setSp(new URLSearchParams(params), { replace: true });
    setAiSummary(summary);
  };

  let listings = data?.listings || [];
  if (filters.saved) listings = listings.filter((l) => saved.includes(l.listing_key));
  if (boundsFilter && bounds) listings = listings.filter((l) => inBounds(l, bounds));
  const active = listings.find((l) => l.listing_key === activeKey) || (data?.listings || []).find((l) => l.listing_key === activeKey);

  useEffect(() => {
    if (base) setFocus({ lng: base.lng, lat: base.lat, zoom: zoomForRadius(radius), pitch: 45, bearing: -10, duration: 2000, id: `b-${base.id}-${radius}` });
  }, [base?.id, radius]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (base || boundsFilter || !data?.listings?.length) return;
    setFocus({ bounds: boundsOf(data.listings), padding: 80, maxZoom: 12.5, id: `fit-${JSON.stringify(apiParams)}` });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pinClick = (key) => {
    setActiveKey(key);
    document.getElementById(`card-${key}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };

  const lifestyleLabel = LIFESTYLES.find((x) => x.id === filters.lifestyle)?.label;
  const heading = base ? `Homes within ${radius} mi of ${base.short}` : filters.saved ? "Your saved homes" : lifestyleLabel ? `${lifestyleLabel}${filters.region ? ` · ${filters.region}` : ""}` : filters.q ? `Homes matching “${filters.q}”` : filters.region ? `${filters.region} homes` : "Hampton Roads & Richmond homes";

  return (
    <div className="flex h-[calc(100dvh-64px)] flex-col" data-testid="search-page">
      <AiSearchBar onResult={applyAi} summary={aiSummary} initialQuery={aiQuery}
        onClear={() => { setAiSummary(""); setSp(new URLSearchParams(), { replace: true }); }} />
      <FilterBar filters={filters} update={update} bases={bases} reset={() => { setAiSummary(""); setSp(new URLSearchParams(), { replace: true }); }} />
      <div className="relative flex min-h-0 flex-1">
        <section className={`${view === "map" ? "hidden" : "flex"} w-full min-w-0 flex-col lg:flex lg:w-[46%] xl:w-[42%]`} data-testid="search-results">
          <div className="flex-1 overflow-y-auto px-4 pb-24 pt-5 sm:px-6 lg:pb-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="text-2xl font-normal text-ink" data-testid="search-heading">{heading}</h1>
                <p className="mt-1 text-[14px] text-neutral-500" data-testid="search-count">{loading ? "Searching…" : `${listings.length} homes`}{base ? " · sorted by distance" : ""}</p>
              </div>
              <Select value={filters.sort || (base ? "distance" : "newest")} onValueChange={(v) => update({ sort: v })}>
                <SelectTrigger className="h-9 w-[190px] rounded-full text-[13px]" data-testid="search-sort"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.filter(([v]) => v !== "distance" || base).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {data?.sample && (
              <div className="mt-4 flex items-start gap-3 rounded-md bg-mist p-3.5 text-[13px] text-neutral-700" data-testid="sample-notice">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-brass-deep" />
                <span>
                  You're viewing <strong className="font-medium">sample listings</strong> for demonstration. Live REIN MLS listings appear here automatically once the feed is connected.
                  <a href={meta?.matrix_url || MLS_SEARCH_URL} target="_blank" rel="noopener noreferrer" data-testid="matrix-search-link"
                    className="mt-2 flex w-fit items-center gap-1 font-medium text-ink underline underline-offset-4 hover:decoration-brass">
                    Search every active REIN listing now <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </span>
              </div>
            )}
            {listings.length > 0 && <div className="mt-4"><MarketSnapshot listings={listings} compact testId="search-snapshot" /></div>}
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {listings.map((l) => (
                <ListingCard key={l.listing_key} listing={l} baseShort={base?.short} onHover={setHoverKey} active={l.listing_key === activeKey} />
              ))}
            </div>
            {!loading && listings.length === 0 && (
              <div className="mt-10 rounded-md border border-neutral-200 p-8 text-center" data-testid="search-empty">
                <p className="text-lg">No homes match these filters.</p>
                <p className="mt-1 text-[14px] text-neutral-600">Try widening the radius or clearing a filter.</p>
                <button onClick={() => setSp(new URLSearchParams(), { replace: true })} className="mt-4 rounded-full bg-ink px-5 py-2 text-[14px] text-white" data-testid="search-empty-reset">Clear filters</button>
              </div>
            )}
            <div className="mt-6"><SearchForMe /></div>
          </div>
        </section>

        <section className={`${view === "list" ? "hidden" : "block"} relative min-w-0 flex-1 lg:block`} data-testid="search-map-panel">
          <HomeMap
            testId="search-map"
            listings={listings}
            bases={bases}
            selectedBaseId={base?.id}
            radius={radius}
            hoverKey={hoverKey}
            activeKey={activeKey}
            onListingClick={pinClick}
            onBaseClick={(id) => update({ base: id, radius: filters.radius || "15", sort: "distance" })}
            onBoundsChange={setBounds}
            focus={focus}
            initialMode="streets"
            initialView={{ center: [-76.3, 36.95], zoom: 9.2, pitch: 40, bearing: 0 }}
            showBaseLabels
          />
          <label className="absolute left-3 top-3 z-10 flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[13px] shadow-lg" data-testid="bounds-filter-toggle">
            <Switch checked={boundsFilter} onCheckedChange={setBoundsFilter} data-testid="bounds-filter-switch" /> Search as I move the map
          </label>
          {active && <ActiveListingCard listing={active} baseShort={base?.short} onClose={() => setActiveKey(null)} />}
        </section>

        <button onClick={() => setView(view === "list" ? "map" : "list")} data-testid="search-view-toggle"
          className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full bg-ink px-5 py-3 text-[14px] text-white shadow-2xl lg:hidden">
          {view === "list" ? <><MapIcon className="h-4 w-4" /> Map</> : <><List className="h-4 w-4" /> List</>}
        </button>
      </div>
    </div>
  );
}
